import {Material} from 'components/customer/Materials/entity';
import {useLazyListMaterialsQuery} from 'components/customer/Materials/store/materialApi';
import {selectMaterialByIndex} from 'components/customer/Materials/store/materialSlice';
import {useNotificationContext} from 'contexts/NotificationContext';
import {useField} from 'formik';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {shallowEqual} from 'react-redux';
import {genericMessageHandler, getCabinetFields} from 'shared/helpers';
import {useAppSelector} from 'store/customer';
import {AppState} from 'store/customer/storeSetup';
import SelectField, {LabelOption} from 'shared/components/SelectField';
import {useGetQFPProductStructureQuery} from 'components/customer/Product/store/productApi';
import {useSearchParams} from 'react-router-dom';
import {useGetUserQuery} from 'components/customer/Auth/store/userSlice';

// this is always 0 for product editor, but if this needs to be implemented later for QFP this
// needs to be dynamic based of product's index on qfp list
const CURRENT_PRODUCT_INDEX = 0;
const FIELD_NAME = 'simple_shelves_thickness';

const thicknessOption = (thickness: number): LabelOption => ({
    label: `${thickness}mm`,
    value: String(thickness),
});

export const ShelfThickness = () => {
    const [searchParams] = useSearchParams();

    const {notify} = useNotificationContext();
    const {data: user} = useGetUserQuery();
    const [options, setOptions] = useState<LabelOption[]>();
    const {
        data: formStructure,
        isLoading: isLoadingFormData,
        isUninitialized,
        isFetching: isFetchingFormData,
    } = useGetQFPProductStructureQuery(
        {
            qfp: false,
            cabinetType: parseInt(searchParams.get('product')),
        },
        {
            skip: !searchParams.has('product'),
        }
    );
    const materialFields = useMemo(() => {
        if (formStructure) {
            return getCabinetFields(formStructure.originalStructure);
        }
    }, [formStructure]);

    const [{value}, , {setValue: setFieldValue}] = useField<number>(FIELD_NAME);

    const [fetchMaterials, {isLoading, isFetching}] =
        useLazyListMaterialsQuery();

    const materialInfo = useAppSelector(
        (state: AppState) =>
            selectMaterialByIndex(state, CURRENT_PRODUCT_INDEX),
        shallowEqual
    );

    const materialId = useRef<number>();

    const customChangeHandler = useCallback(
        (name: string, value: string, options: {value: string}[]) => {
            const option = options.find((option) => option.value == value);

            if (option) {
                void setFieldValue(Number(option.value));
            }
        },
        []
    );

    const fetchThicknesses = useCallback(
        async (material: Material) => {
            if (material && materialId.current != material.id) {
                const isMaterialChanged =
                    typeof materialId.current !== 'undefined';
                materialId.current = material.id;

                const materialType = material.type_id ?? material.type?.id;

                try {
                    const {data, error} = await fetchMaterials(
                        {
                            keywords: material.name,
                            brands: [material.brand_id].map(String),
                            finishes: [material.finish],
                            substrate: material.substrate,
                            materialType: materialType
                                ? String(materialType)
                                : undefined,
                            manufacturerId: user?.manufacturerId,
                            currentPage: 1,
                            pageSize: 1000,
                        },
                        true
                    );

                    if (error) {
                        // eslint-disable-next-line no-console -- just logging error and showing customer clean message
                        console.log(error);
                        throw new Error(
                            'Could not fetch available thickness please try again later'
                        );
                    }

                    if (data && data.data) {
                        const thicknesses: LabelOption[] = data.data
                            .map((material) =>
                                thicknessOption(material.thickness)
                            )
                            .sort((a, b) => Number(a.value) - Number(b.value));

                        const seenThickness = new Set();
                        const unique = thicknesses.filter((thickness) => {
                            const duplicate = seenThickness.has(
                                thickness.value
                            );

                            seenThickness.add(thickness.value);
                            return !duplicate;
                        });

                        if (!unique.length && Number(material.thickness) > 0) {
                            unique.push(thicknessOption(material.thickness));
                        }

                        if (unique.length) {
                            const selectedThickness = unique.find(
                                (option) => option.value == String(value)
                            );

                            if (
                                isMaterialChanged ||
                                selectedThickness == undefined
                            ) {
                                const defaultThickness = unique.find(
                                    (option) =>
                                        Number(option.value) ==
                                        Number(material.thickness)
                                );

                                void setFieldValue(
                                    Number(
                                        (
                                            defaultThickness ??
                                            selectedThickness ??
                                            unique[0]
                                        ).value
                                    )
                                );
                            }
                        }

                        setOptions(unique);
                    }
                } catch (e) {
                    materialId.current = undefined;
                    genericMessageHandler(notify, e);
                }
            }
        },
        [notify, value, user]
    );

    useEffect(() => {
        if (materialInfo && materialFields && user) {
            if (materialFields.hasExterior && !materialFields.hasCarcase) {
                void fetchThicknesses(materialInfo.material);
            } else {
                void fetchThicknesses(materialInfo.carcase);
            }
        }
    }, [materialInfo, materialFields, user]);

    return (
        <SelectField
            id="shelf_thickness"
            name={FIELD_NAME}
            options={options}
            customOnChange={customChangeHandler}
            isLoading={
                isLoading ||
                isLoadingFormData ||
                isUninitialized ||
                isFetching ||
                isFetchingFormData
            }
            placeholder="Select shelf thickness"
            isQFP={false}
        />
    );
};
